import { type IList } from './models';

export type Pagination = {
    pagination?: {
        number: number;
        size: number;
    };
};

/**
 * Fetches and aggregates all pages from a paginated API.
 *
 * - Iterates until no more data is returned
 * - Assumes page-based pagination (`number` + `size`)
 * - Stops when a page is empty, smaller than `size`, or the response's page metadata reports the final page
 *
 * @template T - Item type
 * @template Params - May include `pagination: { number, size }`
 *
 * @param fetchPage - Function to fetch a single page
 * @param params - Request params (mutates `pagination.number`)
 *
 * @returns All items as a flat array
 *
 * @warning Initializes `params.pagination` when missing and mutates `params.pagination.number`
 */
export async function fetchAllWithPagination<T, Params extends Pagination>(
    fetchPage: (params: Params) => Promise<IList<T>>,
    params: Params
): Promise<T[]> {
    params.pagination = params.pagination ?? { number: 0, size: 0 };

    const allItems: T[] = [];
    let pageNumber = 0;
    const pageSize = params.pagination.size;

    while (true) {
        params.pagination.number = pageNumber;

        const page = await fetchPage(params);

        if (!page?.items?.length) {
            break;
        }

        allItems.push(...page.items);

        if (page.items.length < pageSize) {
            break;
        }

        const totalPages = page.page?.totalPages;
        if (Number.isInteger(totalPages) && totalPages >= 0 && pageNumber >= totalPages - 1) {
            break;
        }

        pageNumber++;
    }

    return allItems;
}
